import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import useT from '../../i18n';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Editorial Scandinavian NavBar — Facelift Fase 1 (Feb 2026).
 *
 * Rules:
 *  - Sticky top, h-16 desktop / h-14 mobile
 *  - On `/`: transparent at top, morphs to `bg-paper/80 backdrop-blur` after
 *    scrollY > 8px (subtle threshold — hero content is short-fold)
 *  - On any other route: always solid `bg-paper` + border-b
 *  - Wordmark-only brand ("Norvig" in Fraunces display, no icon)
 *  - Center-ish nav = 4 short items (For fagarbeidere / For kommuner / Om oss / Om Norge)
 *  - NO / EN toggle uses inline `|`-separator, no pill background
 *  - Auth: Logg inn (text-link) + Registrer deg (ink pill). Logged-in user
 *    gets an editorial dropdown → Dashboard / Min profil / Logg ut
 *  - Mobile: custom hamburger → fullscreen paper overlay with big display
 *    typography (Framer Motion opacity+y transition, Escape to close)
 *  - Accessibility: focus rings (2px fjord/40 offset-2), Tab-order logical,
 *    aria-labels on interactive controls
 */
export default function NavBar({ onNavigate, scrollToSection, language, changeLanguage }) {
  const t = useT();
  const navigateFallback = useNavigate();
  const location = useLocation();
  const langCtx = useLanguage();
  const { user, logout } = useAuth();

  const safeNavigate = typeof onNavigate === 'function' ? onNavigate : navigateFallback;
  const safeScrollTo = typeof scrollToSection === 'function'
    ? scrollToSection
    : (id) => { if (typeof window !== 'undefined') window.location.href = `/#${id}`; };
  const safeLanguage = language || langCtx.language;
  const safeChangeLanguage = typeof changeLanguage === 'function' ? changeLanguage : langCtx.changeLanguage;

  const isLanding = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loginMenuOpen, setLoginMenuOpen] = useState(false);

  useEffect(() => {
    // Editorial spec: 8px threshold — instant subtle morph, no delay
    if (!isLanding) { setScrolled(false); return; }
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isLanding]);

  // Escape closes mobile overlay + menus
  useEffect(() => {
    if (!mobileOpen && !userMenuOpen && !loginMenuOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        setUserMenuOpen(false);
        setLoginMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, userMenuOpen, loginMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
    setLoginMenuOpen(false);
  }, [location.pathname]);

  // Solid on non-landing routes; transparent-then-morphed on landing
  const isSolid = !isLanding || scrolled;

  const NAV_LINKS = [
    { id: '/for-kommuner', label: t.nav.forEmployers,      kind: 'route' },
    { id: '/historier',         label: t.nav.stories,           kind: 'route' },
    { id: '/horisont',          label: t.nav.horisont,          kind: 'route' },
    { id: '/om-oss',            label: t.nav.aboutUs,           kind: 'route' },
    { id: '/om-norge',          label: t.nav.aboutNorway,       kind: 'route' },
    { id: '/kontakt',           label: t.nav.contact,           kind: 'route' },
  ];

  // Editorial spec: subtle underline for active route — never on `/`, never
  // on CTA-links (/signup / /login). We treat /signup as CTA even though it's
  // in NAV_LINKS: it's the "For fagarbeidere"-copy but functionally still the
  // signup CTA. Route `/register` is a redirect alias to /signup (see App.js).
  const CTA_PATHS = new Set(['/signup', '/login', '/register']);
  const isActiveRoute = (path) => {
    if (path === '/' || CTA_PATHS.has(path)) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleNav = useCallback((l) => {
    setMobileOpen(false);
    if (l.kind === 'route') { safeNavigate(l.id); return; }
    const el = typeof document !== 'undefined' ? document.getElementById(l.id) : null;
    if (el) { safeScrollTo(l.id); return; }
    if (typeof window !== 'undefined') window.location.assign(`/#${l.id}`);
  }, [safeNavigate, safeScrollTo]);

  const handleLogout = useCallback(async () => {
    setUserMenuOpen(false);
    try { await logout?.(); } catch (e) { /* ignore */ }
    safeNavigate('/');
  }, [logout, safeNavigate]);

  return (
    <>
      <motion.header
        initial={{ y: -8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          isLanding
            ? (scrolled
                ? 'bg-paper/80 backdrop-blur-sm border-b border-ink/[0.08]'
                : 'bg-transparent border-b border-transparent')
            : 'bg-paper border-b border-ink/[0.08]'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-editorial mx-auto px-6 md:px-10 h-14 md:h-16 flex items-center gap-6">
          {/* Wordmark — left (with logo image for brand recognition) */}
          <button
            onClick={() => {
              if (location.pathname !== '/') safeNavigate('/');
              else window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="-ml-1 p-2 -m-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper transition-opacity hover:opacity-70"
            aria-label={t.common?.brandHomeLabel || 'Norvig — hjem'}
            data-testid="navbar-brand"
          >
            {/* Bronze brand wordmark — consistent with footer (Logo Konsistens, Aug 2026) */}
            <img
              src="/brand/norvig-wordmark-bronze-2026.png"
              alt="Norvig"
              className="h-6 md:h-7 w-auto select-none"
              draggable={false}
              data-testid="navbar-logo"
            />
          </button>

          {/* Center-ish nav — desktop only */}
          <nav className="hidden md:flex items-center gap-8 mx-auto" aria-label={t.nav.openMenu}>
            {NAV_LINKS.map((l) => {
              const active = isActiveRoute(l.id);
              return (
                <button
                  key={l.id}
                  onClick={() => handleNav(l)}
                  aria-current={active ? 'page' : undefined}
                  className={`font-sans text-sm tracking-refined transition-colors rounded-sm pb-1 -mb-1 border-b focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper ${
                    active
                      ? 'text-ink border-ink/40'
                      : 'text-ink/70 hover:text-ink border-transparent'
                  }`}
                  data-testid={`nav-link-${l.id.replace('/', '')}`}
                >
                  {l.label}
                </button>
              );
            })}
          </nav>

          {/* Right cluster: lang + auth — desktop */}
          <div className="hidden md:flex items-center gap-5 ml-auto">
            <LangToggleMinimal
              language={safeLanguage}
              changeLanguage={safeChangeLanguage}
              label={t.nav.selectLanguage}
            />

            {user ? (
              <UserMenu
                user={user}
                open={userMenuOpen}
                setOpen={setUserMenuOpen}
                onDashboard={() => { setUserMenuOpen(false); safeNavigate('/dashboard'); }}
                onProfile={() => { setUserMenuOpen(false); safeNavigate('/profil'); }}
                onLogout={handleLogout}
                labels={{
                  dashboard: 'Dashboard',
                  profile: t.profile?.title || 'Min profil',
                  logout: 'Logg ut',
                  menu: t.nav.selectLanguage,
                }}
              />
            ) : (
              <LoginMenu
                t={t}
                open={loginMenuOpen}
                setOpen={setLoginMenuOpen}
                onGo={(path) => { setLoginMenuOpen(false); safeNavigate(path); }}
              />
            )}
          </div>

          {/* Mobile hamburger — right */}
          <button
            className={`md:hidden ml-auto p-2 -mr-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper transition-colors ${
              isSolid ? 'text-ink' : 'text-paper'
            }`}
            onClick={() => setMobileOpen(true)}
            aria-label={t.nav.openMenu}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {/* Custom hamburger — two thin strokes, editorial refined */}
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <line x1="3" y1="7"  x2="17" y2="7"  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="3" y1="13" x2="17" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </motion.header>

      {/* Mobile fullscreen overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <MobileOverlay
            t={t}
            navLinks={NAV_LINKS}
            language={safeLanguage}
            changeLanguage={safeChangeLanguage}
            user={user}
            isActiveRoute={isActiveRoute}
            onNavigate={(l) => handleNav(l)}
            onGo={(path) => { setMobileOpen(false); safeNavigate(path); }}
            onDashboard={() => { setMobileOpen(false); safeNavigate('/dashboard'); }}
            onProfile={() => { setMobileOpen(false); safeNavigate('/profil'); }}
            onLogout={handleLogout}
            onClose={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-components

const LOGIN_GROUPS = (t) => [
  {
    key: 'pro',
    label: t.nav.professionalRole,
    login: '/login?role=professional',
    signup: '/signup?role=professional',
  },
  {
    key: 'kommune',
    label: t.nav.municipalityRole,
    login: '/login?role=employer',
    signup: '/signup?role=employer',
  },
];

function LoginMenu({ t, open, setOpen, onGo }) {
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="px-5 py-2 rounded-full text-sm font-medium bg-ink text-paper hover:bg-ink/90 transition-colors inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
        data-testid="nav-login-menu-toggle"
      >
        {t.nav.login}
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
          <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <AnimatePresence>
        {open && (
          <>
            <button
              className="fixed inset-0 z-40 cursor-default"
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => setOpen(false)}
              data-testid="nav-login-menu-backdrop"
            />
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              role="menu"
              className="absolute right-0 top-full mt-2 w-60 z-50 bg-paper border border-ink/10 rounded-lg shadow-lg overflow-hidden"
              data-testid="nav-login-menu"
            >
              {LOGIN_GROUPS(t).map((g, gi) => (
                <div key={g.key} className={gi > 0 ? 'border-t border-ink/[0.08]' : ''}>
                  <p className="px-4 pt-3 pb-1 font-sans text-[11px] uppercase tracking-[0.18em] font-medium text-ink/45">
                    {g.label}
                  </p>
                  <button
                    onClick={() => onGo(g.login)}
                    className="w-full text-left px-4 py-2.5 font-sans text-sm text-ink/80 hover:bg-ink/[0.03] hover:text-ink transition-colors"
                    data-testid={`nav-login-${g.key}-login`}
                  >
                    {t.nav.login}
                  </button>
                  <button
                    onClick={() => onGo(g.signup)}
                    className="w-full text-left px-4 pt-2.5 pb-3 font-sans text-sm text-ink/80 hover:bg-ink/[0.03] hover:text-ink transition-colors"
                    data-testid={`nav-login-${g.key}-signup`}
                  >
                    {t.nav.registerNew || t.nav.register}
                  </button>
                </div>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function LangToggleMinimal({ language, changeLanguage, label }) {
  return (
    <div role="group" aria-label={label} className="inline-flex items-center gap-1.5 font-sans text-sm">
      {[
        { code: 'no', txt: 'NO' },
        { code: 'en', txt: 'EN' },
      ].map((l, i) => (
        <React.Fragment key={l.code}>
          {i === 1 && (
            <span aria-hidden className="text-ink/20">|</span>
          )}
          <button
            onClick={() => changeLanguage(l.code)}
            aria-pressed={language === l.code}
            className={`transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper ${
              language === l.code
                ? 'text-ink font-medium'
                : 'text-ink/40 hover:text-ink/70'
            }`}
            data-testid={`nav-lang-${l.code}`}
          >
            {l.txt}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

function UserMenu({ user, open, setOpen, onDashboard, onProfile, onLogout, labels }) {
  const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase();
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="w-9 h-9 rounded-full inline-flex items-center justify-center font-sans text-sm font-medium tracking-refined bg-ink/10 text-ink hover:bg-ink/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
        data-testid="nav-user-menu-toggle"
      >
        {initial}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            role="menu"
            className="absolute right-0 top-full mt-2 w-52 bg-paper border border-ink/10 rounded-lg shadow-lg overflow-hidden"
            data-testid="nav-user-menu"
          >
            <button
              onClick={onDashboard}
              className="w-full text-left px-4 py-3 font-sans text-sm text-ink/80 hover:bg-ink/[0.03] hover:text-ink transition-colors"
              data-testid="nav-user-menu-dashboard"
            >
              {labels.dashboard}
            </button>
            <button
              onClick={onProfile}
              className="w-full text-left px-4 py-3 font-sans text-sm text-ink/80 hover:bg-ink/[0.03] hover:text-ink transition-colors border-t border-ink/[0.06]"
              data-testid="nav-user-menu-profile"
            >
              {labels.profile}
            </button>
            <button
              onClick={onLogout}
              className="w-full text-left px-4 py-3 font-sans text-sm text-ink/80 hover:bg-ink/[0.03] hover:text-ink transition-colors border-t border-ink/[0.06]"
              data-testid="nav-user-menu-logout"
            >
              {labels.logout}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileOverlay({
  t, navLinks, language, changeLanguage, user, isActiveRoute,
  onNavigate, onGo, onDashboard, onProfile, onLogout, onClose,
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] bg-paper flex flex-col"
      data-testid="mobile-nav-overlay"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay-header — matches navbar height for visual continuity */}
      <div className="h-14 md:h-16 px-6 flex items-center justify-between border-b border-ink/[0.08]">
        <img
          src="/brand/norvig-wordmark-ink-2026.png"
          alt="Norvig"
          className="h-6 w-auto select-none"
          draggable={false}
        />
        <button
          onClick={onClose}
          aria-label={t.nav.closeMenu}
          className="p-2 -mr-2 rounded-md text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
          data-testid="mobile-nav-close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="16" y1="4" x2="4" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Nav-links: big display-typography, staggered fade-in */}
      <nav className="flex-1 px-6 md:px-10 py-10 flex flex-col">
        {navLinks.map((l, i) => {
          const active = typeof isActiveRoute === 'function' ? isActiveRoute(l.id) : false;
          return (
            <motion.button
              key={l.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: 0.06 + i * 0.04 }}
              onClick={() => { onNavigate(l); onClose(); }}
              aria-current={active ? 'page' : undefined}
              className={`text-left font-display text-3xl tracking-display py-5 border-b focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper rounded-sm ${
                active
                  ? 'text-ink border-ink/40'
                  : 'text-ink border-ink/10'
              }`}
              data-testid={`mobile-nav-${l.id.replace('/', '')}`}
            >
              {l.label}
            </motion.button>
          );
        })}
      </nav>

      {/* Overlay-footer: auth CTAs + lang toggle */}
      <div className="border-t border-ink/[0.08] px-6 md:px-10 py-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <LangToggleMinimal
            language={language}
            changeLanguage={changeLanguage}
            solid={true}
            label={t.nav.selectLanguage}
          />
        </div>
        {user ? (
          <div className="flex flex-col gap-2">
            <button
              onClick={onDashboard}
              className="w-full px-5 py-3 rounded-full text-sm font-medium bg-ink text-paper hover:bg-ink/90 transition-colors"
              data-testid="mobile-nav-dashboard"
            >
              Dashboard
            </button>
            <button
              onClick={onProfile}
              className="w-full px-5 py-3 rounded-full text-sm font-medium border border-ink/15 text-ink hover:bg-ink/[0.04] transition-colors"
              data-testid="mobile-nav-profile"
            >
              {t.profile?.title || 'Min profil'}
            </button>
            <button
              onClick={onLogout}
              className="w-full px-5 py-3 text-sm text-ink/60 hover:text-ink transition-colors"
              data-testid="mobile-nav-logout"
            >
              Logg ut
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {LOGIN_GROUPS(t).map((g) => (
              <div key={g.key}>
                <p className="font-sans text-[11px] uppercase tracking-[0.18em] font-medium text-ink/45 mb-2">
                  {g.label}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => onGo(g.login)}
                    className="flex-1 px-4 py-3 rounded-full text-sm font-medium bg-ink text-paper hover:bg-ink/90 transition-colors"
                    data-testid={`mobile-login-${g.key}-login`}
                  >
                    {t.nav.login}
                  </button>
                  <button
                    onClick={() => onGo(g.signup)}
                    className="flex-1 px-4 py-3 rounded-full text-sm font-medium border border-ink/15 text-ink hover:bg-ink/[0.04] transition-colors"
                    data-testid={`mobile-login-${g.key}-signup`}
                  >
                    {t.nav.register}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
